import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useAppContext, useAppActions } from '../../context/AppContext';
import { getCurrentFacility, getWardsByFacility, formatDate, formatTime, getFilteredAlerts } from '../../utils/helpers';
import { facilityStats, residents, medicationAlerts, automatedWorkflows } from '../../data/mockData';
import useFacilityFeatures from '../../hooks/useFacilityFeatures';
import { Tooltip, EmptyState } from '../Common/ReusableUI';
import { CardSkeleton, GridSkeleton } from '../Common/SkeletonLoaders';
import { useToast } from '../Common/ToastProvider';
import { 
  Bell, Activity, AlertTriangle, 
  Users, Sparkles, TrendingUp, Clock, 
  Sun, Cloud, CloudRain, UserPlus, AlertCircle, Pill,
  Heart, Shield, Zap, Target, BarChart3, Calendar,
  CheckCircle, Star, Video, Globe, Smartphone, Monitor, Gift,
  Brain as BrainIcon, TrendingUp as TrendingUpIcon,
  Eye, RefreshCw, Inbox
} from 'lucide-react';
import QualityMetrics from './QualityMetrics';
import TrendAnalysis from '../Analytics/TrendAnalysis';
import DashboardTrendCharts from './DashboardTrendCharts';
import AssessmentTrendCharts from '../Clinical/AssessmentTrendCharts';
import SettlingInDashboardPanel from './SettlingInDashboardPanel';
import NotificationEngine from '../../services/notifications/NotificationEngine';

const TIME_RANGES = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'This Week' },
  { key: 'month', label: 'This Month' },
];

const KPI_TOOLTIPS = {
  residents: 'Current census including respite and permanent residents',
  medRounds: 'Completed medication administration rounds today',
  alerts: 'Unresolved clinical and compliance alerts requiring attention',
  family: 'Active family portal sessions today',
};

const KPI_NAV_MAP = {
  residents: 'residents',
  medRounds: 'medication',
  alerts: 'alerts',
  family: 'family-portal',
};

const Dashboard = () => {
  const { state } = useAppContext();
  const actions = useAppActions();
  const { showToast } = useToast();
  const { 
    currentUser, 
    selectedFacility, 
    currentTime, 
    weatherData, 
    deviceInfo
  } = state;
  
  const { isAgedCare } = useFacilityFeatures();

  const [isLoading, setIsLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('today');

  useEffect(() => {
    if (selectedFacility && selectedFacility !== 'all') {
      try {
        NotificationEngine.runFullScan(selectedFacility);
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[Dashboard] notification scan failed', e);
        }
      }
    }
  }, [selectedFacility]);

  const [liveData, setLiveData] = useState({
    activeResidents: 0,
    medicationRounds: 0,
    alertsResolved: 0,
    familyConnections: 0
  });

  const [showTrendAnalysis, setShowTrendAnalysis] = useState(false);

  const aiPredictiveData = {
    healthOutcomes: {
      fallRisk: { score: 23, trend: 'decreasing', confidence: 87, residents: 3 },
      readmissionRisk: { score: 12, trend: 'stable', confidence: 89, residents: 2 },
      medicationAdherence: { score: 94.2, trend: 'improving', confidence: 92, residents: 15 },
      healthDeterioration: { score: 8, trend: 'decreasing', confidence: 85, residents: 1 }
    },
    operationalInsights: {
      staffingOptimization: { efficiency: 94.2, costSavings: 12.3, satisfaction: 4.6 },
      resourceUtilization: { optimal: 18, underutilized: 2, overutilized: 1 },
      qualityMetrics: { compliance: 98.5, improvement: 15.2, benchmarks: 95.8 },
      costOptimization: { savings: 18.2, efficiency: 23.1, wasteReduction: 8.7 }
    },
    predictions: {
      next7Days: {
        highRiskEvents: 3,
        resourceNeeds: 2,
        qualityIssues: 1,
        costSavings: 5.2
      },
      next30Days: {
        healthTrends: 'improving',
        operationalEfficiency: 'stable',
        qualityImprovement: 'expected',
        costReduction: 'projected'
      }
    },
    recommendations: [
      {
        id: 'AI_DASH_001',
        type: 'Health',
        priority: 'High',
        title: 'Implement fall prevention program for 3 high-risk residents',
        description: 'AI detected elevated fall risk in 3 residents. Recommend immediate intervention.',
        impact: 'Reduce fall risk by 35%',
        confidence: 94,
        timeframe: 'Next 7 days'
      },
      {
        id: 'AI_DASH_002',
        type: 'Operational',
        priority: 'Medium',
        title: 'Optimize staff scheduling for weekend coverage',
        description: 'AI identified weekend staffing gaps. Recommend schedule adjustments.',
        impact: 'Improve coverage by 20%',
        confidence: 87,
        timeframe: 'Next 14 days'
      },
      {
        id: 'AI_DASH_003',
        type: 'Quality',
        priority: 'Low',
        title: 'Enhance medication documentation process',
        description: 'AI suggests improving medication documentation to reduce errors.',
        impact: 'Reduce documentation errors by 15%',
        confidence: 78,
        timeframe: 'Next 30 days'
      }
    ]
  };

  const facilityInfo = useMemo(() => getCurrentFacility(selectedFacility), [selectedFacility]);
  const currentWards = useMemo(() => {
    if (selectedFacility === 'all') {
      const { wardsByFacility } = require('../../data/mockData');
      return Object.values(wardsByFacility).flat();
    }
    return getWardsByFacility(selectedFacility);
  }, [selectedFacility]);
  const stats = useMemo(() => facilityStats[selectedFacility] || {}, [selectedFacility]);

  const dashboardData = useMemo(() => {
    const facilityResidents = residents.filter(r => r.facility === selectedFacility || selectedFacility === 'all');
    const todayAlerts = medicationAlerts.filter(a => a.facility === selectedFacility || selectedFacility === 'all');
    const activeWorkflows = automatedWorkflows.filter(w => w.status === 'Active');
    
    const rangeMultiplier = timeRange === 'week' ? 7 : timeRange === 'month' ? 30 : 1;
    
    return {
      totalResidents: facilityResidents.length,
      activeAlerts: todayAlerts.length,
      criticalAlerts: todayAlerts.filter(a => a.priority === 'High').length,
      workflowsActive: activeWorkflows.length,
      avgAdherence: facilityResidents.reduce((acc, r) => {
        const avgPrescriptionAdherence = r.prescriptions?.reduce((pAcc, p) => pAcc + (p.adherenceRate || 0), 0) / (r.prescriptions?.length || 1);
        return acc + avgPrescriptionAdherence;
      }, 0) / facilityResidents.length || 0,
      todayActivities: Math.floor(Math.random() * 8) + 3,
      familyConnections: facilityResidents.reduce((acc, r) => acc + (r.familyMembers?.length || 0), 0),
      medicationRounds: (Math.floor(Math.random() * 12) + 8) * (timeRange === 'week' ? 5 : timeRange === 'month' ? 22 : 1),
      incidentsToday: Math.floor(Math.random() * 3),
      occupancyRate: stats.occupancy || 94.5,
      timeRange,
    };
  }, [selectedFacility, stats, timeRange]);

  const careCompliancePct = useMemo(() => {
    const list = residents.filter((r) => selectedFacility === 'all' || r.facility === selectedFacility);
    const rates = list
      .map((r) => r.careMinutes?.monthly?.complianceRate)
      .filter((x) => typeof x === 'number' && !Number.isNaN(x));
    if (rates.length) {
      return Math.min(100, Math.round(rates.reduce((a, b) => a + b, 0) / rates.length));
    }
    return 96;
  }, [selectedFacility]);

  const facilityAlertsActive = useMemo(
    () =>
      medicationAlerts
        .filter((a) => selectedFacility === 'all' || a.facility === selectedFacility)
        .filter((a) => a.status === 'Active'),
    [selectedFacility]
  );

  const facilityAlertsPreview = useMemo(
    () => facilityAlertsActive.slice(0, 8),
    [facilityAlertsActive]
  );

  const starRatingDisplay = useMemo(() => {
    const base = 4.0 + (stats.medicationAdherence ? Math.min(0.9, stats.medicationAdherence / 120) : 0.2);
    return Math.min(5, Math.round(base * 10) / 10);
  }, [stats]);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    console.debug('[Dashboard] compliance snapshot', {
      facility: selectedFacility,
      careCompliancePct,
      starRatingDisplay,
      activeAlertsPreview: facilityAlertsActive.length
    });
  }, [selectedFacility, careCompliancePct, starRatingDisplay, facilityAlertsActive.length]);

  // Simulate initial data load
  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 800);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setLiveData({
        activeResidents: Math.floor(Math.random() * 5) + dashboardData.totalResidents - 2,
        medicationRounds: Math.floor(Math.random() * 3) + dashboardData.medicationRounds,
        alertsResolved: Math.floor(Math.random() * 2) + dashboardData.activeAlerts - 1,
        familyConnections: Math.floor(Math.random() * 3) + dashboardData.familyConnections - 1
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [dashboardData]);

  const handleKpiClick = useCallback((kpiKey) => {
    const tab = KPI_NAV_MAP[kpiKey];
    if (tab) {
      actions.setActiveTab(tab);
      if (process.env.NODE_ENV === 'development') {
        console.debug('[Dashboard] KPI drill-down →', tab);
      }
    }
  }, [actions]);

  const handleRefresh = useCallback(() => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      showToast('Dashboard data refreshed', 'success');
    }, 600);
  }, [showToast]);

  const handleTimeRangeChange = useCallback((range) => {
    setTimeRange(range);
    showToast(`Showing ${TIME_RANGES.find(t => t.key === range)?.label || range} data`, 'info', 2000);
  }, [showToast]);

  const liveAlerts = useMemo(
    () => getFilteredAlerts(selectedFacility, 'all', currentUser).slice(0, 3),
    [selectedFacility, currentUser]
  );

  if (isLoading) {
    return (
      <div className="space-y-6 will-change-auto">
        <div className="rounded-3xl bg-gradient-to-br from-indigo-200 via-purple-200 to-pink-200 animate-pulse h-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
        </div>
        <GridSkeleton items={4} />
      </div>
    );
  }

  return (
    <div className="space-y-6 will-change-auto">
      {/* Hero Section with Live Data */}
      <div className={`relative overflow-hidden rounded-3xl p-8 text-white shadow-2xl bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600`}>
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-white opacity-10 rounded-full animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-white opacity-10 rounded-full animate-pulse delay-1000"></div>
        </div>
        
        <div className="relative z-10">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-6">
            {/* Greeting + weather */}
            <div className="shrink-0">
              <h1 className="text-2xl xl:text-3xl font-bold mb-1 flex items-center gap-2">
                Good {currentTime.getHours() < 12 ? 'Morning' : currentTime.getHours() < 17 ? 'Afternoon' : 'Evening'}, {currentUser?.name}
                <Sparkles className="w-6 h-6 text-yellow-300 animate-pulse" />
              </h1>
              <p className="text-sm opacity-90">
                {facilityInfo.name} Command Center
              </p>
              <p className="text-xs opacity-80 mt-1 flex items-center gap-2">
                <Clock className="w-3.5 h-3.5" />
                {formatDate(currentTime)} • {formatTime(currentTime)}
                <span className="ml-1 inline-flex items-center gap-1">
                  {weatherData.condition === 'sunny' && <Sun className="w-3.5 h-3.5 text-yellow-300" />}
                  {weatherData.condition === 'cloudy' && <Cloud className="w-3.5 h-3.5 text-gray-300" />}
                  {weatherData.condition === 'rainy' && <CloudRain className="w-3.5 h-3.5 text-blue-300" />}
                  {weatherData.temp}°C
                </span>
              </p>

              {/* Time Range Selector + Refresh */}
              <div className="flex items-center gap-2 mt-3">
                <div className="inline-flex rounded-lg bg-white/20 p-0.5">
                  {TIME_RANGES.map(({ key, label }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => handleTimeRangeChange(key)}
                      className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${
                        timeRange === key
                          ? 'bg-white text-indigo-700 shadow-sm'
                          : 'text-white/80 hover:bg-white/10'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={handleRefresh}
                  className="p-1.5 rounded-lg bg-white/15 hover:bg-white/25 transition-colors"
                  aria-label="Refresh dashboard data"
                >
                  <RefreshCw className="w-3.5 h-3.5 text-white" />
                </button>
              </div>
            </div>

            {/* Live Metrics + Quick Actions (right column) */}
            <div className="flex-1 min-w-0 lg:max-w-[60%] space-y-3">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                {/* Residents KPI */}
                <Tooltip content={KPI_TOOLTIPS.residents} position="bottom">
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => handleKpiClick('residents')}
                    onKeyDown={(e) => e.key === 'Enter' && handleKpiClick('residents')}
                    className="bg-white/95 backdrop-blur rounded-lg px-2 py-4 shadow border border-white/20 cursor-pointer hover:shadow-lg transition-shadow"
                  >
                    <div className="flex flex-col items-center text-center gap-1.5">
                      <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg">
                        <Users className="w-5 h-5 text-white" />
                      </div>
                      <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide leading-tight">Residents</p>
                      <p className="text-2xl font-bold text-gray-900 leading-none">{liveData.activeResidents}</p>
                      <p className="text-xs text-green-600 font-medium flex items-center gap-0.5">
                        <TrendingUp className="w-3.5 h-3.5" />+2.3%
                      </p>
                    </div>
                  </div>
                </Tooltip>

                {/* Med Rounds KPI */}
                <Tooltip content={KPI_TOOLTIPS.medRounds} position="bottom">
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => handleKpiClick('medRounds')}
                    onKeyDown={(e) => e.key === 'Enter' && handleKpiClick('medRounds')}
                    className="bg-white/95 backdrop-blur rounded-lg px-2 py-4 shadow border border-white/20 cursor-pointer hover:shadow-lg transition-shadow"
                  >
                    <div className="flex flex-col items-center text-center gap-1.5">
                      <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg">
                        <Pill className="w-5 h-5 text-white" />
                      </div>
                      <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide leading-tight">Med Rounds</p>
                      <p className="text-2xl font-bold text-gray-900 leading-none">{liveData.medicationRounds}</p>
                      <p className="text-xs text-green-600 font-medium flex items-center gap-0.5">
                        <CheckCircle className="w-3.5 h-3.5" />98%
                      </p>
                    </div>
                  </div>
                </Tooltip>

                {/* Alerts KPI */}
                <Tooltip content={KPI_TOOLTIPS.alerts} position="bottom">
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => handleKpiClick('alerts')}
                    onKeyDown={(e) => e.key === 'Enter' && handleKpiClick('alerts')}
                    className="bg-white/95 backdrop-blur rounded-lg px-2 py-4 shadow border border-white/20 cursor-pointer hover:shadow-lg transition-shadow"
                  >
                    <div className="flex flex-col items-center text-center gap-1.5">
                      <div className="p-2 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg">
                        <AlertTriangle className="w-5 h-5 text-white" />
                      </div>
                      <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide leading-tight">Alerts</p>
                      <p className="text-2xl font-bold text-gray-900 leading-none">{liveData.alertsResolved}</p>
                      <p className="text-xs text-blue-600 font-medium flex items-center gap-0.5">
                        <Shield className="w-3.5 h-3.5" />All clear
                      </p>
                    </div>
                  </div>
                </Tooltip>

                {/* Family KPI */}
                <Tooltip content={KPI_TOOLTIPS.family} position="bottom">
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => handleKpiClick('family')}
                    onKeyDown={(e) => e.key === 'Enter' && handleKpiClick('family')}
                    className="bg-white/95 backdrop-blur rounded-lg px-2 py-4 shadow border border-white/20 cursor-pointer hover:shadow-lg transition-shadow"
                  >
                    <div className="flex flex-col items-center text-center gap-1.5">
                      <div className="p-2 bg-gradient-to-br from-pink-500 to-pink-600 rounded-lg">
                        <Heart className="w-5 h-5 text-white" />
                      </div>
                      <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide leading-tight">Family</p>
                      <p className="text-2xl font-bold text-gray-900 leading-none">{liveData.familyConnections}</p>
                      <p className="text-xs text-green-600 font-medium flex items-center gap-0.5">
                        <Heart className="w-3.5 h-3.5" />Active
                      </p>
                    </div>
                  </div>
                </Tooltip>
              </div>

              {/* Quick Actions */}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => actions.setShowNewEnquiry(true)}
                  className="touch-target px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg text-sm font-semibold hover:from-blue-700 hover:to-blue-800 transition-all shadow-md hover:shadow-lg inline-flex items-center gap-1.5"
                >
                  <UserPlus className="w-4 h-4 shrink-0" />
                  New Enquiry
                </button>
                <button
                  type="button"
                  onClick={() => actions.setActiveTab('residents')}
                  className="touch-target px-4 py-2 bg-gradient-to-r from-cyan-600 to-cyan-700 text-white rounded-lg text-sm font-semibold hover:from-cyan-700 hover:to-cyan-800 transition-all shadow-md hover:shadow-lg inline-flex items-center gap-1.5"
                >
                  <Users className="w-4 h-4 shrink-0" />
                  Residents
                </button>
                <button
                  type="button"
                  onClick={() => actions.setActiveTab('medication')}
                  className="touch-target px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg text-sm font-semibold hover:from-purple-700 hover:to-purple-800 transition-all shadow-md hover:shadow-lg inline-flex items-center gap-1.5"
                >
                  <Pill className="w-4 h-4 shrink-0" />
                  Medication Round
                </button>
                <button
                  type="button"
                  onClick={() => actions.setActiveTab('alerts')}
                  className="touch-target px-4 py-2 bg-gradient-to-r from-orange-600 to-orange-700 text-white rounded-lg text-sm font-semibold hover:from-orange-700 hover:to-orange-800 transition-all shadow-md hover:shadow-lg inline-flex items-center gap-1.5"
                >
                  <Bell className="w-4 h-4 shrink-0" />
                  View Alerts
                </button>
                <button
                  type="button"
                  onClick={() => actions.setActiveTab('quality')}
                  className="touch-target px-4 py-2 bg-gradient-to-r from-indigo-600 to-indigo-800 text-white rounded-lg text-sm font-semibold hover:from-indigo-700 hover:to-indigo-900 transition-all shadow-md hover:shadow-lg inline-flex items-center gap-1.5"
                >
                  <Shield className="w-4 h-4 shrink-0" />
                  Quality
                </button>
                <button
                  type="button"
                  onClick={() => actions.setActiveTab('telehealth')}
                  className="touch-target px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg text-sm font-semibold hover:from-green-700 hover:to-green-800 transition-all shadow-md hover:shadow-lg inline-flex items-center gap-1.5"
                >
                  <Video className="w-4 h-4 shrink-0" />
                  Telehealth
                </button>
              </div>
            </div>
          </div>

          {/* Admission & Settling-In Panel */}
          {isAgedCare && selectedFacility && selectedFacility !== 'all' && (
            <div className="mb-6">
              <SettlingInDashboardPanel />
            </div>
          )}
        </div>
      </div>

      {/* Phase 1 — compliance snapshot: dense strip + compact alerts */}
      <div className="mf-card rounded-xl p-4 mb-4 border border-gray-200/80 shadow-sm">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 mb-3">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
            <Shield className="w-4 h-4 text-indigo-600 shrink-0" aria-hidden />
            Facility compliance snapshot
          </h3>
          <span className="text-xs text-gray-400">Refreshes every 5s</span>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-stretch gap-3 lg:gap-0">
          <div className="grid grid-cols-2 gap-3 sm:gap-4 flex-1 min-w-0 lg:pr-4">
            <Tooltip content="Care minutes compliance (30-day roll-up) against AN-ACC targets">
              <div className="min-w-0 rounded-lg bg-gray-50/80 px-2.5 py-2 border border-gray-100">
                <div className="flex items-center justify-between gap-1 mb-1">
                  <span
                    className="text-[11px] font-medium uppercase tracking-wide text-gray-600 truncate"
                    title="Care minutes compliance (30-day roll-up)"
                  >
                    Care minutes
                  </span>
                  <span className="text-sm font-bold text-emerald-700 tabular-nums shrink-0">{careCompliancePct}%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all duration-500"
                    style={{ width: `${careCompliancePct}%` }}
                  />
                </div>
                <p className="text-[10px] text-gray-500 mt-1 leading-snug line-clamp-2">
                  AN-ACC adjusted resident-days → 100% target
                </p>
              </div>
            </Tooltip>
            <Tooltip content="Star rating composite from staffing, quality and compliance indicators">
              <div className="min-w-0 rounded-lg bg-gray-50/80 px-2.5 py-2 border border-gray-100">
                <div className="flex items-center justify-between gap-1 mb-1">
                  <span className="text-[11px] font-medium uppercase tracking-wide text-gray-600 truncate">Star rating</span>
                  <span className="text-sm font-bold text-amber-700 flex items-center gap-0.5 tabular-nums shrink-0">
                    <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-400" aria-hidden />
                    {starRatingDisplay}/5
                  </span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-amber-400 to-orange-400 rounded-full"
                    style={{ width: `${(starRatingDisplay / 5) * 100}%` }}
                  />
                </div>
                <p className="text-[10px] text-gray-500 mt-1 leading-snug">Staffing · quality · compliance (demo)</p>
              </div>
            </Tooltip>
          </div>

          <div className="hidden lg:block w-px bg-gray-200 shrink-0 mx-1" aria-hidden />

          {/* Alerts */}
          <div className="lg:w-[min(100%,18rem)] xl:w-80 lg:shrink-0 min-w-0 flex flex-col border-t border-gray-100 pt-3 lg:border-t-0 lg:pt-0 lg:pl-4 lg:border-l">
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <span className="text-xs font-semibold text-gray-800">Alerts</span>
              {facilityAlertsActive.length > 0 ? (
                <button
                  type="button"
                  onClick={() => actions.setActiveTab('alerts')}
                  className="text-[11px] font-medium text-indigo-600 hover:text-indigo-800 py-1 px-2 rounded-md hover:bg-indigo-50"
                >
                  All ({facilityAlertsActive.length})
                </button>
              ) : null}
            </div>
            <ul className="space-y-1 text-xs max-h-28 overflow-y-auto overscroll-contain pr-0.5">
              {facilityAlertsPreview.length === 0 && (
                <li className="py-4">
                  <EmptyState
                    icon={Bell}
                    title="No active alerts"
                    description="All alerts have been resolved for this facility."
                  />
                </li>
              )}
              {facilityAlertsPreview.map((a) => (
                <li key={a.id} className="flex gap-1.5 border-l-2 border-orange-400 pl-1.5 py-0.5 bg-orange-50/70 rounded-r">
                  <AlertTriangle className="w-3.5 h-3.5 text-orange-600 shrink-0 mt-0.5" aria-hidden />
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900 truncate">{a.resident}</div>
                    <div className="text-gray-600 line-clamp-2 leading-snug">{a.issue}</div>
                    <div className="text-[10px] text-gray-400">{a.timestamp}</div>
                  </div>
                </li>
              ))}
            </ul>
            {facilityAlertsActive.length > facilityAlertsPreview.length ? (
              <p className="text-[10px] text-gray-400 mt-1">
                +{facilityAlertsActive.length - facilityAlertsPreview.length} more
              </p>
            ) : null}
          </div>
        </div>
        <DashboardTrendCharts
          careCompliancePct={careCompliancePct}
          starRatingDisplay={starRatingDisplay}
          facilityKey={selectedFacility}
        />
        <AssessmentTrendCharts mode="facility" facilityKey={selectedFacility} phase2Heading />
      </div>

      {/* AI Predictive Analytics - Horizontal Layout */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 mb-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold flex items-center gap-3">
            <BrainIcon className="w-6 h-6 text-purple-600" />
            AI Predictive Analytics
          </h3>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-sm text-green-600 font-medium">AI Active</span>
          </div>
        </div>

        {/* Health Outcomes Prediction */}
        <div className="mb-6">
          <h4 className="text-lg font-semibold text-gray-800 mb-4">Health Outcomes Prediction</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Tooltip content="Aggregate fall risk score from gait analysis, medication side-effects, and environmental factors">
              <div className="bg-gradient-to-br from-red-50 to-red-100 p-4 rounded-xl border border-red-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="p-2 bg-red-500 rounded-lg">
                    <AlertTriangle className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-xs text-red-600 font-medium">↓ {aiPredictiveData.healthOutcomes.fallRisk.trend}</span>
                </div>
                <div className="text-2xl font-bold text-red-600">{aiPredictiveData.healthOutcomes.fallRisk.score}</div>
                <div className="text-sm text-gray-600">Fall Risk Score</div>
                <div className="text-xs text-gray-500">{aiPredictiveData.healthOutcomes.fallRisk.residents} residents</div>
              </div>
            </Tooltip>

            <Tooltip content="Hospital readmission probability within 30 days based on clinical history">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="p-2 bg-blue-500 rounded-lg">
                    <Heart className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-xs text-blue-600 font-medium">↓ {aiPredictiveData.healthOutcomes.readmissionRisk.trend}</span>
                </div>
                <div className="text-2xl font-bold text-blue-600">{aiPredictiveData.healthOutcomes.readmissionRisk.score}%</div>
                <div className="text-sm text-gray-600">Readmission Risk</div>
                <div className="text-xs text-gray-500">{aiPredictiveData.healthOutcomes.readmissionRisk.residents} residents</div>
              </div>
            </Tooltip>

            <Tooltip content="Medication administration adherence rate across all residents">
              <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl border border-green-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="p-2 bg-green-500 rounded-lg">
                    <Pill className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-xs text-green-600 font-medium">↑ {aiPredictiveData.healthOutcomes.medicationAdherence.trend}</span>
                </div>
                <div className="text-2xl font-bold text-green-600">{aiPredictiveData.healthOutcomes.medicationAdherence.score}%</div>
                <div className="text-sm text-gray-600">Medication Adherence</div>
                <div className="text-xs text-gray-500">{aiPredictiveData.healthOutcomes.medicationAdherence.residents} residents</div>
              </div>
            </Tooltip>

            <Tooltip content="Early-warning score for health deterioration using vital-sign trends">
              <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-4 rounded-xl border border-yellow-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="p-2 bg-yellow-500 rounded-lg">
                    <TrendingUpIcon className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-xs text-yellow-600 font-medium">↓ {aiPredictiveData.healthOutcomes.healthDeterioration.trend}</span>
                </div>
                <div className="text-2xl font-bold text-yellow-600">{aiPredictiveData.healthOutcomes.healthDeterioration.score}%</div>
                <div className="text-sm text-gray-600">Health Deterioration</div>
                <div className="text-xs text-gray-500">{aiPredictiveData.healthOutcomes.healthDeterioration.residents} residents</div>
              </div>
            </Tooltip>
          </div>
        </div>

        {/* Operational Insights */}
        <div className="mb-6">
          <h4 className="text-lg font-semibold text-gray-800 mb-4">Operational Insights</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-4 rounded-xl border border-purple-200">
              <div className="flex items-center justify-between mb-3">
                <h5 className="font-semibold text-purple-800">Staffing Optimization</h5>
                <div className="p-2 bg-purple-500 rounded-lg">
                  <Users className="w-4 h-4 text-white" />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Efficiency</span>
                  <span className="font-medium text-purple-600">{aiPredictiveData.operationalInsights.staffingOptimization.efficiency}%</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Cost Savings</span>
                  <span className="font-medium text-green-600">${aiPredictiveData.operationalInsights.staffingOptimization.costSavings}K</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Satisfaction</span>
                  <span className="font-medium text-blue-600">{aiPredictiveData.operationalInsights.staffingOptimization.satisfaction}/5</span>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-xl border border-green-200">
              <div className="flex items-center justify-between mb-3">
                <h5 className="font-semibold text-green-800">Quality Metrics</h5>
                <div className="p-2 bg-green-500 rounded-lg">
                  <Target className="w-4 h-4 text-white" />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Compliance</span>
                  <span className="font-medium text-green-600">{aiPredictiveData.operationalInsights.qualityMetrics.compliance}%</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Improvement</span>
                  <span className="font-medium text-blue-600">+{aiPredictiveData.operationalInsights.qualityMetrics.improvement}%</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Benchmarks</span>
                  <span className="font-medium text-purple-600">{aiPredictiveData.operationalInsights.qualityMetrics.benchmarks}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* AI Recommendations */}
        <div>
          <h4 className="text-lg font-semibold text-gray-800 mb-4">AI Recommendations</h4>
          {aiPredictiveData.recommendations.length === 0 ? (
            <EmptyState
              icon={BrainIcon}
              title="No recommendations"
              description="AI has no outstanding recommendations at this time."
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {aiPredictiveData.recommendations.map((recommendation) => (
                <div key={recommendation.id} className="p-4 bg-gray-50 rounded-xl border border-gray-200 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h5 className="font-semibold text-sm">{recommendation.title}</h5>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          recommendation.priority === 'High' ? 'bg-red-100 text-red-800' :
                          recommendation.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {recommendation.priority}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 mb-2">{recommendation.description}</p>
                      <div className="space-y-1 text-xs text-gray-500">
                        <div className="flex items-center gap-1">
                          <Target className="w-3 h-3" />
                          {recommendation.impact}
                        </div>
                        <div className="flex items-center gap-1">
                          <BrainIcon className="w-3 h-3" />
                          {recommendation.confidence}% confidence
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {recommendation.timeframe}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                      <Eye className="w-4 h-4" />
                    </button>
                    <button className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg">
                      <CheckCircle className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Enhanced Widget Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        

        {/* Automated Workflows */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold flex items-center gap-2">
              <Zap className="w-5 h-5 text-green-600" />
              Workflows
            </h3>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-xs text-green-600 font-medium">Active</span>
            </div>
          </div>
          <div className="space-y-4">
            <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
              <div className="flex items-center justify-between mb-2">
                <p className="font-semibold text-green-900">Prescription Renewal</p>
                <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">95%</span>
              </div>
              <p className="text-sm text-green-700 mb-2">Automated renewal alerts</p>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-600">2 hrs/week saved</span>
              </div>
            </div>
            <div className="p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border border-blue-200">
              <div className="flex items-center justify-between mb-2">
                <p className="font-semibold text-blue-900">Family Updates</p>
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">98%</span>
              </div>
              <p className="text-sm text-blue-700 mb-2">Automated notifications</p>
              <div className="flex items-center gap-2">
                <Heart className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-600">3 hrs/week saved</span>
              </div>
            </div>
          </div>
        </div>

        {/* Critical Alerts */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 flex flex-col min-h-[280px]">
          <div className="flex items-center justify-between mb-4 flex-shrink-0">
            <h3 className="text-lg font-bold flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              Critical Alerts
            </h3>
            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
          </div>
          <div className="space-y-4 flex-grow">
            <div className="p-4 bg-gradient-to-r from-red-50 to-pink-50 rounded-xl border border-red-200">
              <div className="flex items-center justify-between mb-2">
                <p className="font-semibold text-red-900">Medication Expiring</p>
                <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">5</span>
              </div>
              <p className="text-sm text-red-700 mb-2">Prescriptions need renewal</p>
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-600" />
                <span className="text-sm font-medium text-red-600">Action required</span>
              </div>
            </div>
            <div className="p-4 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl border border-orange-200">
              <div className="flex items-center justify-between mb-2">
                <p className="font-semibold text-orange-900">Fall Risk</p>
                <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full">2</span>
              </div>
              <p className="text-sm text-orange-700 mb-2">Residents at risk</p>
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-orange-600" />
                <span className="text-sm font-medium text-orange-600">Monitor closely</span>
              </div>
            </div>
          </div>
        </div>

        {/* Telehealth Quick Access */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 flex flex-col min-h-[280px]">
          <div className="flex items-center justify-between mb-4 flex-shrink-0">
            <h3 className="text-lg font-bold flex items-center gap-2">
              <Video className="w-5 h-5 text-purple-600" />
              Telehealth
            </h3>
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
          </div>
          <div className="space-y-4 flex-grow">
            <div className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200">
              <div className="flex items-center justify-between mb-2">
                <p className="font-semibold text-purple-900">Active Sessions</p>
                <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full">3</span>
              </div>
              <p className="text-sm text-purple-700 mb-2">Video consultations</p>
              <div className="flex items-center gap-2">
                <Monitor className="w-4 h-4 text-purple-600" />
                <span className="text-sm font-medium text-purple-600">HD Quality</span>
              </div>
            </div>
            <div className="p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border border-blue-200">
              <div className="flex items-center justify-between mb-2">
                <p className="font-semibold text-blue-900">Patient Portal</p>
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">89%</span>
              </div>
              <p className="text-sm text-blue-700 mb-2">Active users</p>
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-600">24/7 Access</span>
              </div>
            </div>
          </div>
        </div>

        {/* Today's Activities */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 flex flex-col min-h-[280px]">
          <div className="flex items-center justify-between mb-4 flex-shrink-0">
            <h3 className="text-lg font-bold flex items-center gap-2">
              <Calendar className="w-5 h-5 text-indigo-600" />
              Today's Activities
            </h3>
            <button 
              onClick={() => actions.setActiveTab('activities')}
              className="text-xs text-indigo-600 hover:underline"
            >
              View all
            </button>
          </div>
          <div className="flex-grow">
            {dashboardData.todayActivities === 0 ? (
              <EmptyState
                icon={Calendar}
                title="No activities scheduled"
                description="There are no activities planned for today."
              />
            ) : (
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-gradient-to-r from-indigo-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Activity className="w-8 h-8 text-indigo-600" />
                </div>
                <p className="text-3xl font-bold text-gray-800 mb-2">{dashboardData.todayActivities}</p>
                <p className="text-sm text-gray-500">activities scheduled</p>
                <div className="mt-4 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Morning Tai Chi</span>
                    <span className="text-green-600 font-medium">9:00 AM</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Music Therapy</span>
                    <span className="text-blue-600 font-medium">2:00 PM</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Family Visits</span>
                    <span className="text-purple-600 font-medium">4:00 PM</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Performance Metrics */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-emerald-600" />
              Performance
            </h3>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-xs text-green-600 font-medium">Excellent</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Tooltip content="Bed occupancy rate across all wards in selected facility">
              <div className="text-center p-4 bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl border border-emerald-200">
                <p className="text-2xl font-bold text-emerald-600">{dashboardData.occupancyRate.toFixed(0)}%</p>
                <p className="text-xs text-emerald-600 font-medium">Occupancy</p>
                <div className="flex items-center justify-center gap-1 mt-1">
                  <TrendingUp className="w-3 h-3 text-green-600" />
                  <span className="text-xs text-green-600">+2.3%</span>
                </div>
              </div>
            </Tooltip>
            <Tooltip content="Average medication administration adherence across all residents">
              <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                <p className="text-2xl font-bold text-blue-600">{dashboardData.avgAdherence.toFixed(0)}%</p>
                <p className="text-xs text-blue-600 font-medium">Adherence</p>
                <div className="flex items-center justify-center gap-1 mt-1">
                  <TrendingUp className="w-3 h-3 text-green-600" />
                  <span className="text-xs text-green-600">+1.2%</span>
                </div>
              </div>
            </Tooltip>
          </div>
        </div>

        {/* Mobile App Stats */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-pink-600" />
              Mobile App
            </h3>
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 text-yellow-500" />
              <span className="text-xs text-yellow-600 font-medium">4.8</span>
            </div>
          </div>
          <div className="space-y-4">
            <div className="p-4 bg-gradient-to-r from-pink-50 to-rose-50 rounded-xl border border-pink-200">
              <div className="flex items-center justify-between mb-2">
                <p className="font-semibold text-pink-900">Downloads</p>
                <span className="text-xs bg-pink-100 text-pink-800 px-2 py-1 rounded-full">1,247</span>
              </div>
              <p className="text-sm text-pink-700 mb-2">Patient app users</p>
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-pink-600" />
                <span className="text-sm font-medium text-pink-600">+23% growth</span>
              </div>
            </div>
            <div className="p-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl border border-yellow-200">
              <div className="flex items-center justify-between mb-2">
                <p className="font-semibold text-yellow-900">Loyalty Program</p>
                <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">$2,340</span>
              </div>
              <p className="text-sm text-yellow-700 mb-2">Rewards distributed</p>
              <div className="flex items-center gap-2">
                <Gift className="w-4 h-4 text-yellow-600" />
                <span className="text-sm font-medium text-yellow-600">89% engaged</span>
              </div>
            </div>
          </div>
        </div>

        {/* Real-time Alerts */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 flex flex-col min-h-[280px]">
          <div className="flex items-center justify-between mb-4 flex-shrink-0">
            <h3 className="text-lg font-bold flex items-center gap-2">
              <Bell className="w-5 h-5 text-amber-600" />
              Live Alerts
            </h3>
            <button 
              onClick={() => actions.setActiveTab('alerts')}
              className="text-xs text-amber-600 hover:underline"
            >
              View all
            </button>
          </div>
          <div className="space-y-3 flex-grow">
            {liveAlerts.length === 0 ? (
              <EmptyState
                icon={Inbox}
                title="No live alerts"
                description="Everything is running smoothly right now."
              />
            ) : (
              liveAlerts.map((alert) => (
                <div key={alert.id} className="flex items-center justify-between p-3 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl border border-amber-200">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
                    <div>
                      <p className="text-sm font-medium text-amber-900 capitalize">{alert.type}</p>
                      <p className="text-xs text-amber-700">{alert.resident}</p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                    alert.priority === 'High' ? 'bg-red-100 text-red-800' : 
                    alert.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' : 
                    'bg-green-100 text-green-800'
                  }`}>
                    {alert.priority}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

      </div>

      {/* Trend Analysis Section */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-200 mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-2xl font-bold text-indigo-800 flex items-center gap-3">
              <BarChart3 className="w-7 h-7 text-indigo-600" />
              Trend Analysis & Historical Data
            </h3>
            <p className="text-indigo-600 mt-1">Comprehensive analytics and historical data visualization for informed decision making</p>
          </div>
          <button
            onClick={() => setShowTrendAnalysis(true)}
            className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2 shadow-lg"
          >
            <BarChart3 className="w-5 h-5" />
            Open Trend Analysis
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg border border-indigo-200 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <Pill className="w-5 h-5 text-blue-600" />
              <h4 className="font-semibold text-gray-800">Medication Adherence</h4>
            </div>
            <p className="text-sm text-gray-600">Track medication compliance trends and identify improvement opportunities</p>
          </div>
          
          <div className="bg-white p-4 rounded-lg border border-green-200 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <Target className="w-5 h-5 text-green-600" />
              <h4 className="font-semibold text-gray-800">Quality Metrics</h4>
            </div>
            <p className="text-sm text-gray-600">Monitor quality indicators and compliance trends over time</p>
          </div>
          
          <div className="bg-white p-4 rounded-lg border border-red-200 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <h4 className="font-semibold text-gray-800">Incident Analysis</h4>
            </div>
            <p className="text-sm text-gray-600">Analyze incident patterns and safety improvement trends</p>
          </div>
          
          <div className="bg-white p-4 rounded-lg border border-purple-200 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-5 h-5 text-purple-600" />
              <h4 className="font-semibold text-gray-800">Staff Performance</h4>
            </div>
            <p className="text-sm text-gray-600">Track staff satisfaction and performance metrics</p>
          </div>
        </div>
      </div>

      {/* Quality Metrics Summary */}
      {(currentUser?.role === 'manager' || currentUser?.role === 'admin') && (
        <QualityMetrics 
          facilityInfo={facilityInfo}
          deviceInfo={deviceInfo}
        />
      )}

      {/* Trend Analysis Modal */}
      {showTrendAnalysis && (
        <TrendAnalysis onClose={() => setShowTrendAnalysis(false)} />
      )}
      
    </div>
  );
};

export default Dashboard;
